import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VIDEO_EXT_RE = /\.(mp4|webm|mov)(\?.*)?$/i;

const isVideoBanner = (banner) =>
  banner.media_type === "video" ||
  banner.is_video === true ||
  VIDEO_EXT_RE.test(banner.media_url || banner.image_url || "");

const trackBanner = (bannerId, event) => {
  if (!bannerId || !bannerId.startsWith("banner_")) return;
  try {
    const payload = JSON.stringify({ banner_id: bannerId, event });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API_URL}/api/banners/track`,
        new Blob([payload], { type: "application/json" })
      );
    } else {
      axios.post(`${API_URL}/api/banners/track`, { banner_id: bannerId, event });
    }
  } catch {
    /* tracking must never break the UI */
  }
};

export default function BannerCarousel({ banners = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const seenRef = useRef(new Set());

  // Default banners if none provided
  const displayBanners = banners.length > 0 ? banners : [
    {
      banner_id: "default1",
      image_url: "https://images.unsplash.com/photo-1585756511089-4d495bb55a11?w=1920&h=600&fit=crop",
      title: "Găsește mașina perfectă",
      link_url: "/category/cars"
    },
    {
      banner_id: "default2",
      image_url: "https://images.unsplash.com/photo-1680503146454-0fe569cef4eb?w=1920&h=600&fit=crop",
      title: "Imobiliare de top",
      link_url: "/category/real_estate"
    },
    {
      banner_id: "default3",
      image_url: "https://images.unsplash.com/photo-1745970649957-b4b1f7fde4ea?w=1920&h=600&fit=crop",
      title: "Locuri de muncă",
      link_url: "/category/jobs"
    }
  ];

  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % displayBanners.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [displayBanners.length, isTransitioning]);

  const goToPrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + displayBanners.length) % displayBanners.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  // Auto-rotate: 4.5s for images, 10s for video banners (give the clip time to play)
  const currentIsVideo = isVideoBanner(displayBanners[currentIndex] || {});
  useEffect(() => {
    const interval = setInterval(() => {
      goToNext();
    }, currentIsVideo ? 10000 : 4500);
    return () => clearInterval(interval);
  }, [goToNext, currentIsVideo]);

  // Impression tracking — one per banner per page view
  const currentBannerId = (displayBanners[currentIndex] || {}).banner_id;
  useEffect(() => {
    if (currentBannerId && !seenRef.current.has(currentBannerId)) {
      seenRef.current.add(currentBannerId);
      trackBanner(currentBannerId, "impression");
    }
  }, [currentBannerId]);

  if (displayBanners.length === 0) return null;

  return (
    <div className="relative w-full h-[300px] md:h-[400px] lg:h-[500px] overflow-hidden rounded-2xl" data-testid="banner-carousel">
      {/* Banners */}
      {displayBanners.map((banner, index) => (
        <a
          key={banner.banner_id}
          href={banner.link_url || "#"}
          onClick={() => trackBanner(banner.banner_id, "click")}
          className={`absolute inset-0 transition-all duration-500 ease-out ${
            index === currentIndex 
              ? "opacity-100 scale-100" 
              : "opacity-0 scale-105"
          }`}
          data-testid={`banner-${index}`}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10" />
          {isVideoBanner(banner) ? (
            <video
              src={banner.media_url || banner.image_url}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload={index === 0 ? "auto" : "metadata"}
              data-testid={`banner-video-${index}`}
            />
          ) : (
            <img
              src={banner.image_url || banner.media_url}
              alt={banner.title || "Banner promoțional"}
              className="w-full h-full object-cover"
              loading={index === 0 ? "eager" : "lazy"}
              fetchpriority={index === 0 ? "high" : "low"}
              decoding="async"
            />
          )}
          {banner.title && (
            <div className="absolute bottom-8 left-8 z-20">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
                {banner.title}
              </h2>
            </div>
          )}
        </a>
      ))}

      {/* Navigation Arrows */}
      {displayBanners.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            aria-label="Banner anterior"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition-colors"
            data-testid="banner-prev-btn"
          >
            <ChevronLeft className="w-6 h-6" aria-hidden="true" />
          </button>
          <button
            onClick={goToNext}
            aria-label="Banner următor"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition-colors"
            data-testid="banner-next-btn"
          >
            <ChevronRight className="w-6 h-6" aria-hidden="true" />
          </button>
        </>
      )}

      {/* Dots */}
      {displayBanners.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
          {displayBanners.map((b, index) => (
            <button
              key={b.banner_id || b.image_url}
              aria-label={`Treci la banner-ul ${index + 1}`}
              onClick={() => {
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setCurrentIndex(index);
                  setTimeout(() => setIsTransitioning(false), 500);
                }
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? "w-8 bg-white" 
                  : "w-2 bg-white/40 hover:bg-white/60"
              }`}
              data-testid={`banner-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
